<template>
  <div class="my-favorite-page">
    <div class="favorite-header">
      <button class="back-btn" @click="$router.go(-1)">
        <van-icon name="arrow-left" />
      </button>
      <div>
        <div class="header-title">{{ pageTitle }}</div>
        <div class="header-desc">共 {{ favoriteList.length }} 位心仪对象</div>
      </div>
    </div>

    <div class="favorite-content" v-if="favoriteList.length > 0">
      <div class="favorite-grid">
        <div class="favorite-card" v-for="item in favoriteList" :key="item.memberId + '-' + (item.source || 'fav')">
          <div class="card-image" @click="viewDetail(item)">
            <img :src="item.avatarUrl || defaultPhoto" alt="">
          </div>

          <div class="card-body">
            <div class="card-title-row">
              <span class="card-code">编号：{{ item.code || formatSerial(item.serialNumber) }}</span>
              <span class="gender-chip">{{ item.sex === 1 ? '男' : '女' }}</span>
            </div>

            <div class="card-meta">
              <span>{{ item.age }}岁</span>
              <span>{{ item.height || '-' }}cm</span>
              <span>{{ getEducationName(item.education) }}</span>
            </div>

            <div class="activity-tag" v-if="item.source === 'activity' && item.activityName">来自：{{ item.activityName }}</div>
          </div>

          <div class="card-actions">
            <button class="ghost-btn" @click="viewDetail(item)">查看详情</button>
            <button v-if="!isReadonly" class="danger-btn" @click="handleCancelLike(item)">{{ item.source === 'activity' ? '取消喜欢' : '取消收藏' }}</button>
          </div>
        </div>
      </div>
    </div>

    <van-loading v-if="loading" type="spinner" color="#FF2593" class="loading-more" />

    <div v-if="!loading && favoriteList.length === 0" class="empty-state">
      <div class="empty-icon">💕</div>
      <div class="empty-text">暂无心仪对象</div>
      <div class="empty-hint">{{ emptyHint }}</div>
    </div>
  </div>
</template>

<script>
import { Toast, Dialog } from "vant";
import { getFavoriteMembers, removeFavorite } from "@/api/memberFavorite";
import { getAllMyFavoriteMembers, cancelMutualChoice } from "@/api/activityInfo";
import { mapGetters } from "vuex";
import defaultPhoto from "../assets/defaultPhoto.png";

export default {
  data() {
    return {
      favoriteList: [],
      loading: false,
      defaultPhoto: defaultPhoto,
      educationData: ["大专", "本科", "硕士", "博士", "高中及以下"]
    };
  },
  computed: {
    ...mapGetters(["baseInfo", "role"]),
    isReadonly() {
      return this.$route.query.readonly === "1";
    },
    targetMemberId() {
      return this.isReadonly ? this.$route.query.memberId : (this.baseInfo && this.baseInfo.id);
    },
    pageTitle() {
      if (!this.isReadonly) {
        return "我的心仪对象";
      }
      const memberName = this.$route.query.memberName;
      return memberName ? `${memberName}的心仪对象` : "心仪对象";
    },
    emptyHint() {
      return this.isReadonly ? "该会员暂未选择心仪对象" : "快去遇见页面收藏心仪对象吧";
    }
  },
  methods: {
    getEducationName(education) {
      if (!education) return "-";
      const index = education - 1;
      return this.educationData[index] || "-";
    },
    loadFavoriteList() {
      if (this.loading) return;
      this.loading = true;
      const memberId = this.targetMemberId;
      if (!memberId) {
        this.favoriteList = [];
        this.loading = false;
        return;
      }
      // 同时加载活动互选心仪和通用收藏
      Promise.all([
        getAllMyFavoriteMembers(memberId),
        getFavoriteMembers(memberId)
      ])
        .then(([activityRes, favoriteRes]) => {
          const seen = new Set();
          const list = [];

          // 先处理活动互选（优先级更高，先放入列表）
          if (activityRes.code === 20000 && activityRes.data) {
            (activityRes.data || []).forEach(item => {
              const key = String(item.memberId);
              if (seen.has(key)) return;
              seen.add(key);
              list.push({
                memberId: item.memberId,
                name: item.name,
                sex: item.sex,
                age: item.age,
                height: item.height,
                education: item.education,
                avatarUrl: item.avatarUrl,
                code: item.sceneCode || "未分配",
                source: "activity",
                activityName: item.activityName,
                activityId: item.activityId
              });
            });
          }

          // 再处理通用收藏（去重，活动已有的跳过）
          if (favoriteRes.code === 20000 && favoriteRes.data) {
            (favoriteRes.data || []).forEach(item => {
              const key = String(item.memberId);
              if (seen.has(key)) return;
              seen.add(key);
              list.push({
                memberId: item.memberId,
                name: item.name || item.nickName,
                sex: item.sex,
                age: item.age,
                height: item.height,
                education: item.education,
                avatarUrl: item.avatarUrl,
                code: this.formatSerial(item.serialNumber),
                serialNumber: item.serialNumber,
                source: "favorite"
              });
            });
          }

          this.favoriteList = list;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    viewDetail(item) {
      this.$router.push({ path: `/detailInformation/${item.memberId}` });
    },
    handleCancelLike(item) {
      if (this.isReadonly) {
        return;
      }
      const code = item.code || this.formatSerial(item.serialNumber);
      const isActivity = item.source === "activity";
      const actionName = isActivity ? "取消喜欢" : "取消收藏";
      Dialog.confirm({
        message: `确定${actionName}编号 ${code} 的用户吗？`
      })
        .then(() => {
          const memberId = this.baseInfo && this.baseInfo.id;
          let promise;
          if (isActivity) {
            promise = cancelMutualChoice({
              activityId: item.activityId,
              chooserMemberId: memberId,
              canceledMemberId: item.memberId
            });
          } else {
            promise = removeFavorite(memberId, item.memberId);
          }
          promise.then(res => {
            if (res.code === 20000) {
              Toast.success(isActivity ? "已取消喜欢" : "已取消收藏");
              this.favoriteList = this.favoriteList.filter(f =>
                !(f.memberId === item.memberId && f.source === item.source)
              );
            } else {
              Toast.fail(res.message || "取消失败");
            }
          });
        })
        .catch(() => {});
    },
    formatSerial(serialNumber) {
      if (!serialNumber) return "0000";
      return String(serialNumber).padStart(4, "0");
    },
  },
  created() {
    this.loadFavoriteList();
  }
};
</script>

<style lang="less" scoped src="../style/myFavoriteMembers.less" />



// WEBPACK FOOTER //
// src/views/myFavoriteMembers.vue